// UPDATE v0.1 (2025-10-19): Добавлены поля клиента и каскадный выбор типа дела
// File: src/pages/NewCasePage.js

// --- 1. ИМПОРТЫ ---
// Импортируем хуки React для управления состоянием.
import React, { useState } from 'react';
// Импортируем хуки для навигации и получения параметров
import { useNavigate, useLocation } from 'react-router-dom';
// Импортируем библиотеку для отображения уведомлений.
import toast from 'react-hot-toast';
// Импортируем наш централизованный сервис для работы с API.
import apiService from '../services/apiService';
// Импортируем компонент для отображения индикатора загрузки.
import LoadingSpinner from '../components/LoadingSpinner';
// Импортируем стили, специфичные для этой страницы.
import './NewCasePage.css';

// --- ДАННЫЕ ДЛЯ КАСКАДНОГО ВЫБОРА (ВРЕМЕННО) ---
// В будущем это должно приходить с бэкенда
const caseSubtypesMap = {
  Immigration: [
    'Investor Visas (E-2, EB-5)',
    'Employment-Based Visas (L-1, O-1, H-1B)',
    'Family-Based Green Cards',
    'Fiancé(e) Visas (K-1)',
    'Adjustment of Status',
    'Naturalization & Citizenship',
    // Добавьте другие подтипы по мере необходимости
  ],
  "Family Law": [], // Добавьте подтипы при необходимости
  "Real Estate": [], // Добавьте подтипы при необходимости
};
// --- КОНЕЦ ДАННЫХ ---

// --- 2. КОМПОНЕНТ СТРАНИЦЫ ---
const NewCasePage = () => {
    const location = useLocation();
    const init = location.state || {};

    // --- 3. СОСТОЯНИЯ КОМПОНЕНТА (STATE) ---

    // Информация о деле
    const [title, setTitle] = useState(init.title || ''); // Состояние для заголовка дела
    const [description, setDescription] = useState(init.description || ''); // Состояние для описания
    const [caseType, setCaseType] = useState(init.caseType || 'Immigration'); // Тип дела по умолчанию
    const [caseSubtype, setCaseSubtype] = useState(init.caseSubtype || ''); // Состояние для подтипа дела

    // --- ДОБАВЛЕНО: Состояния для информации о клиенте ---
    const [clientEmail, setClientEmail] = useState(init.clientEmail || ''); // Email клиента
    const [clientFirstName, setClientFirstName] = useState(init.clientFirstName || ''); // Имя клиента
    const [clientLastName, setClientLastName] = useState(init.clientLastName || ''); // Фамилия клиента
    const [clientPhoneNumber, setClientPhoneNumber] = useState(init.clientPhoneNumber || ''); // Телефон клиента
    // --- КОНЕЦ ДОБАВЛЕНИЯ ---

    const [isLoading, setIsLoading] = useState(false); // Флаг загрузки
    const navigate = useNavigate(); // Хук для навигации

    // --- Функция для обновления подтипа при смене основного типа ---
    const handleCaseTypeChange = (e) => {
        const newType = e.target.value;
        setCaseType(newType);
        // Сбрасываем подтип, так как он зависит от основного типа
        setCaseSubtype('');
    };

    // --- 4. ОБРАБОТЧИК ОТПРАВКИ ФОРМЫ (ОБНОВЛЕН) ---
    const handleCreateCase = async (e) => {
        // Предотвращаем стандартное поведение формы (перезагрузку страницы).
        e.preventDefault();

        // --- Валидация: email ИЛИ телефон клиента обязателен ---
        const email = clientEmail.trim();
        const phone = clientPhoneNumber.trim();

        if (!email && !phone) {
            toast.error('Client Email or Phone Number is required.');
            return;
        }
        if (!title.trim()) {
            toast.error('Case Title is required.');
            return;
        }

        setIsLoading(true);
        const toastId = toast.loading('Creating new case and client (if needed)...');

        try {
            const caseData = {
                client_email: email || null,
                client_first_name: clientFirstName.trim() || 'Клиент',
                client_last_name: clientLastName.trim() || '',
                client_phone_number: phone || null,
                title: title.trim(),
                description: description.trim(),
                case_type: caseType,
                case_subtype: caseSubtype || null,
            };

            // Вызываем метод API (он уже готов принимать любой объект)
            const response = await apiService.createCase(caseData);

            toast.success('Case created successfully!', { id: toastId }); // Уведомление об успехе

            // "Умное" перенаправление: если API вернул ID, переходим на страницу дела.
            if (response && response.id) {
                navigate(`/cases/${response.id}`);
            } else {
                navigate('/cases'); // Иначе переходим на список дел
            }

        } catch (error) {
            // --- ОБРАБОТКА ОШИБОК ---
            console.error("Failed to create case:", error); // Лог ошибки в консоль
            const errorMessage = error.message || 'An unexpected error occurred.'; // Получаем сообщение об ошибке
            // Отображаем ошибку валидации от Pydantic, если она есть
            toast.error(`Failed to create case: ${errorMessage}`, { id: toastId }); // Уведомление об ошибке
        } finally {
            // В любом случае выключаем индикатор загрузки
            setIsLoading(false);
        }
    };

    // --- 5. УСЛОВНЫЙ РЕНДЕРИНГ ---
    // Показываем спиннер вместо формы во время отправки
    if (isLoading) {
        return <LoadingSpinner message="Submitting your case..." />;
    }

    // Получаем список подтипов для выбранного основного типа
    const currentSubtypes = caseSubtypesMap[caseType] || [];

    // --- 6. JSX-РАЗМЕТКА (ОБНОВЛЕНА) ---
    return (
        <div className="container">
            <div className="card new-case-card">
                <h1 className="card-header">Create a New Case</h1> {/* Заголовок: Создать новое дело */}
                <p className="new-case-subheader">
                    {/* Подзаголовок: Введите данные клиента и дела. Если email новый, профиль будет создан автоматически. */}
                    Enter client and case details below. If the client's email is new, a profile will be created automatically.
                </p>

                <form onSubmit={handleCreateCase}>

                    {/* --- ДОБАВЛЕНО: Секция информации о клиенте --- */}
                    <h2 className="section-header">Client Information</h2> {/* Заголовок секции: Информация о клиенте */}
                    <div className="form-group">
                        <label htmlFor="client-email">Client Email <span className="required">*</span> <span style={{ fontSize: '0.8em', fontWeight: 'normal', color: 'var(--ink-muted-ink, #888)' }}>(Email or Phone required)</span></label>
                        <input
                            id="client-email"
                            type="email"
                            className="form-input"
                            value={clientEmail}
                            onChange={(e) => setClientEmail(e.target.value)}
                            placeholder="e.g., client@example.com"
                        />
                    </div>
                    <div className="form-row"> {/* Используем form-row для полей в одной строке */}
                       <div className="form-group">
                           <label htmlFor="client-first-name">First Name</label> {/* Поле: Имя */}
                           <input
                               id="client-first-name"
                               type="text"
                               className="form-input"
                               value={clientFirstName}
                               onChange={(e) => setClientFirstName(e.target.value)}
                               placeholder="Client's first name" // Подсказка: Имя клиента
                           />
                       </div>
                       <div className="form-group">
                           <label htmlFor="client-last-name">Last Name</label> {/* Поле: Фамилия */}
                           <input
                               id="client-last-name"
                               type="text"
                               className="form-input"
                               value={clientLastName}
                               onChange={(e) => setClientLastName(e.target.value)}
                               placeholder="Client's last name" // Подсказка: Фамилия клиента
                           />
                       </div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="client-phone">Phone Number</label> {/* Поле: Номер телефона */}
                        <input
                            id="client-phone"
                            type="tel" // Используем tel для семантики и мобильной клавиатуры
                            className="form-input"
                            value={clientPhoneNumber}
                            onChange={(e) => setClientPhoneNumber(e.target.value)}
                            placeholder="e.g., +12125551234" // Подсказка: Пример номера
                        />
                    </div>
                    {/* --- КОНЕЦ СЕКЦИИ КЛИЕНТА --- */}


                    {/* --- Секция информации о деле --- */}
                    <h2 className="section-header">Case Details</h2> {/* Заголовок секции: Детали дела */}
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="case-type">Case Type <span className="required">*</span></label> {/* Поле: Тип дела (обязательно) */}
                            <select
                                id="case-type"
                                className="form-input"
                                value={caseType}
                                onChange={handleCaseTypeChange} // Используем новый обработчик
                                required
                            >
                                <option value="Immigration">Immigration</option>
                                <option value="Family Law">Family Law</option>
                                <option value="Real Estate">Real Estate</option>
                                <option value="Estate Planning">Estate Planning</option>
                                {/* Добавьте другие типы */}
                            </select>
                        </div>

                        {/* --- ДОБАВЛЕНО: Каскадный выбор подтипа --- */}
                        {/* Показываем, только если есть подтипы для выбранного типа */}
                        {currentSubtypes.length > 0 && (
                            <div className="form-group">
                                {/* Название поля зависит от основного типа, для примера 'Immigration Subtype' */}
                                <label htmlFor="case-subtype">{caseType === 'Immigration' ? 'Immigration Subtype' : 'Case Subtype'}</label> {/* Поле: Подтип дела */}
                                <select
                                    id="case-subtype"
                                    className="form-input"
                                    value={caseSubtype}
                                    onChange={(e) => setCaseSubtype(e.target.value)}
                                >
                                    <option value="">-- Select Subtype --</option> {/* Опция: Выберите подтип */}
                                    {currentSubtypes.map(subtype => (
                                        <option key={subtype} value={subtype}>{subtype}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {/* --- КОНЕЦ КАСКАДНОГО ВЫБОРА --- */}
                    </div>

                    <div className="form-group">
                        <label htmlFor="case-title">Case Title <span className="required">*</span></label> {/* Поле: Заголовок дела (обязательно) */}
                        <input
                            id="case-title"
                            type="text"
                            className="form-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g., 'Naturalization Application for John Smith'" // Подсказка: Пример заголовка
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="case-description">Description (Optional)</label> {/* Поле: Описание (необязательно) */}
                        <textarea
                            id="case-description"
                            rows="5"
                            className="form-input"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Provide a brief summary of the case..." // Подсказка: Краткое описание
                        />
                    </div>
                    {/* --- КОНЕЦ СЕКЦИИ ДЕЛА --- */}

                    <div className="form-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(-1)} // Кнопка "Cancel" возвращает назад
                            disabled={isLoading}
                        >
                            Cancel {/* Кнопка: Отмена */}
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isLoading}
                        >
                            {/* Текст кнопки меняется во время загрузки */}
                            {isLoading ? 'Creating...' : 'Create Case'} {/* Кнопка: Создать дело / Создание... */}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- 7. ЭКСПОРТ ---
export default NewCasePage;